import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import { Button, Divider, ScrollArea, Text, TextInput } from "@mantine/core";

import { MAX_VARCHAR_LENGTH } from "../../constants/constants";
import { useFormValidation } from "../../hooks/useFormValidation";
import { CreateMailDto } from "../../models/CreateMailDto";
import { Template } from "../../models/Template";
import { useMailMutation } from "../../mutations/useMailMutation";
import classes from "../../styles/components/forms/MailForm.module.scss";
import { ErrorNotification } from "../notification/ErrorNotification";
import { SuccessNotification } from "../notification/SuccessNotification";
import EmailInCcsInput from "./EmailInCcsInput.tsx";

export const TemplatedMailForm = (template: Template) => {
  const templateVariables: CreateMailDto["templateVariables"] = {};
  const { validateEmailFormat } = useFormValidation();
  const [isCCsEnabled, setIsCCsEnabled] = useState(false);
  const [isBCCsEnabled, setIsBCCsEnabled] = useState(false);
  const toggleCCs = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsCCsEnabled((prev) => !prev);
  };
  const toggleBCCs = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsBCCsEnabled((prev) => !prev);
  };
  const [errors, setErrors] = useState<{
    senderEmail?: string;
    receiverEmail?: string;
  }>({});
  template.variables.forEach((variable) => {
    templateVariables[variable] = "";
  });
  const isSubmitDisabled = !!errors.senderEmail || !!errors.receiverEmail;

  const { register, handleSubmit, watch, control } = useForm<CreateMailDto>({
    defaultValues: {
      senderEmail: "",
      receiverEmail: "",
      ccs: [],
      bccs: [],
      templateCode: template.code,
      subject: template.subject,
      body: template.body,
      templateVariables: templateVariables
    }
  });

  const currentSenderEmail = watch("senderEmail");
  const currentReceiverEmail = watch("receiverEmail");

  const validateEmailField = (email: string, field: string) => {
    if (validateEmailFormat(email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: ""
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "Invalid email format"
      }));
    }
  };

  useEffect(() => {
    validateEmailField(currentSenderEmail, "senderEmail");
    validateEmailField(currentReceiverEmail, "receiverEmail");
  }, [currentSenderEmail, currentReceiverEmail]);

  const { saveMailMutation } = useMailMutation();

  const onSubmit = (data: CreateMailDto) => {
    saveMailMutation.mutate(data, {
      onSuccess: () => {
        SuccessNotification("The email has been successfully submitted.");
      },
      onError: () => {
        ErrorNotification("A problem occurred while submitting the email.");
      }
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TextInput error={errors.senderEmail} required label="From" {...register("senderEmail")} />
      <TextInput error={errors.receiverEmail} required label="To" {...register("receiverEmail")} />
      <div>
        <a
          hidden={isCCsEnabled}
          onClick={toggleCCs}
          style={{ cursor: "pointer", color: "grey", paddingRight: "1%" }}
        >
          CCs
        </a>
        <a hidden={isBCCsEnabled} onClick={toggleBCCs} style={{ cursor: "pointer", color: "grey" }}>
          BCCs
        </a>
      </div>
      {isCCsEnabled ? (
        <Controller
          name="ccs"
          control={control}
          render={({ field }) => (
            <EmailInCcsInput
              label={"CCs"}
              onChange={(emails) => field.onChange(emails)}
              value={field.value}
            />
          )}
        />
      ) : (
        <></>
      )}

      {isBCCsEnabled ? (
        <Controller
          name="bccs"
          control={control}
          render={({ field }) => (
            <EmailInCcsInput
              label={"BCCs"}
              onChange={(emails) => field.onChange(emails)}
              value={field.value}
            />
          )}
        />
      ) : (
        <></>
      )}
      <TextInput label="Subject" placeholder="No subject" {...register("subject")} />
      <Text style={{ marginTop: "1rem" }}>Variables</Text>
      <Divider style={{ marginBottom: "0.25rem" }} />
      <ScrollArea.Autosize mah={250}>
        {template.variables.map((variable) => (
          <TextInput
            key={variable}
            placeholder={variable}
            label={variable}
            maxLength={MAX_VARCHAR_LENGTH}
            {...register(`templateVariables.${variable}`)}
          />
        ))}
      </ScrollArea.Autosize>
      <Button className={classes.button} type="submit" disabled={isSubmitDisabled}>
        Submit
      </Button>
    </form>
  );
};
